// Chemin: C:\gestion-copro\src\pages\admin\InterlocuteursPage.tsx
// Session 47 : suppression bouton Modifier redondant

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { 
  Eye, 
  PauseCircle, 
  PlayCircle, 
  Plus,
  Users,
  X
} from 'lucide-react';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { desactiverInterlocuteur, reactiverInterlocuteur } from '../../services/interlocuteur.service';

// Types
interface Contact {
  nom: string;
  prenom?: string;
  fonction?: string;
  email?: string;
  telephone?: string;
  principal: boolean;
}

interface Interlocuteur {
  id: string;
  coproprieteId: string;
  type: 'syndic' | 'promoteur' | 'constructeur' | 'prestataire' | 'assurance' | 'avocat' | 'expert' | 'autre';
  raisonSociale: string;
  siret?: string;
  contacts: Contact[];
  actif: boolean;
  notes?: string;
}

// Labels pour les types d'interlocuteurs
const TYPE_LABELS: Record<string, string> = {
  syndic: 'Syndic',
  promoteur: 'Promoteur',
  constructeur: 'Constructeur',
  prestataire: 'Prestataire',
  assurance: 'Assurance',
  avocat: 'Avocat',
  expert: 'Expert',
  autre: 'Autre'
};

// Couleurs pour les badges de type (selon charte graphique)
const TYPE_COLORS: Record<string, string> = {
  syndic: 'bg-blue-100 text-blue-800',
  promoteur: 'bg-purple-100 text-purple-800',
  constructeur: 'bg-orange-100 text-orange-800',
  prestataire: 'bg-gray-100 text-gray-800',
  assurance: 'bg-emerald-100 text-emerald-800',
  avocat: 'bg-red-100 text-red-800',
  expert: 'bg-yellow-100 text-yellow-800',
  autre: 'bg-gray-100 text-gray-600'
};

export default function InterlocuteursPage() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [interlocuteurs, setInterlocuteurs] = useState<Interlocuteur[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionEnCours, setActionEnCours] = useState<string | null>(null);

  const coproprieteId = userProfile?.coproprieteIds?.[0];

  // Charger tous les interlocuteurs (actifs ET suspendus)
  const fetchInterlocuteurs = async () => {
    if (!coproprieteId) {
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'interlocuteurs'),
        where('coproprieteId', '==', coproprieteId),
        orderBy('raisonSociale')
      );
      
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Interlocuteur[];
      
      // Trier : actifs en premier, puis suspendus
      data.sort((a, b) => {
        if (a.actif === b.actif) {
          return a.raisonSociale.localeCompare(b.raisonSociale);
        }
        return a.actif ? -1 : 1;
      });
      
      setInterlocuteurs(data);
    } catch (err) {
      console.error('Erreur chargement interlocuteurs:', err);
      setError('Erreur lors du chargement des interlocuteurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterlocuteurs();
  }, [coproprieteId]);

  // Récupère le contact principal d'un interlocuteur
  const getContactPrincipal = (interlocuteur: Interlocuteur): Contact | null => {
    if (!interlocuteur.contacts || interlocuteur.contacts.length === 0) {
      return null;
    }
    return interlocuteur.contacts.find(c => c.principal) || interlocuteur.contacts[0];
  };

  // Suspendre un interlocuteur
  const handleSuspendre = async (interlocuteur: Interlocuteur) => {
    const confirmation = window.confirm(
      `Suspendre "${interlocuteur.raisonSociale}" ?\n\nCet interlocuteur n'apparaîtra plus dans les sélecteurs de destinataires.`
    );
    
    if (!confirmation) return;
    
    setActionEnCours(interlocuteur.id);
    try {
      await desactiverInterlocuteur(interlocuteur.id);
      await fetchInterlocuteurs();
    } catch (err) {
      console.error('Erreur suspension:', err);
      setError('Erreur lors de la suspension');
    } finally {
      setActionEnCours(null);
    }
  };

  // Réactiver un interlocuteur
  const handleReactiver = async (interlocuteur: Interlocuteur) => {
    const confirmation = window.confirm(
      `Réactiver "${interlocuteur.raisonSociale}" ?`
    );
    
    if (!confirmation) return;
    
    setActionEnCours(interlocuteur.id);
    try {
      await reactiverInterlocuteur(interlocuteur.id);
      await fetchInterlocuteurs();
    } catch (err) {
      console.error('Erreur réactivation:', err);
      setError('Erreur lors de la réactivation');
    } finally {
      setActionEnCours(null);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Interlocuteurs</h1>
            <p className="text-gray-600 mt-1">
              Gérez vos contacts : syndic, promoteur, prestataires...
            </p>
          </div>
          <Link
            to="/admin/interlocuteurs/nouveau"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Plus className="w-5 h-5 mr-2" />
            Ajouter un interlocuteur
          </Link>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Chargement */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Liste vide */}
        {!loading && interlocuteurs.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun interlocuteur</h3>
            <p className="text-gray-500 mb-6">
              Commencez par ajouter votre syndic et votre promoteur/constructeur.
            </p>
            <Link
              to="/admin/interlocuteurs/nouveau"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Ajouter le premier interlocuteur
            </Link>
          </div>
        )}

        {/* Liste des interlocuteurs */}
        {!loading && interlocuteurs.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Raison sociale
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact principal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {interlocuteurs.map((interlocuteur) => {
                  const contact = getContactPrincipal(interlocuteur);
                  const estSuspendu = !interlocuteur.actif;
                  const enTraitement = actionEnCours === interlocuteur.id;
                  
                  return (
                    <tr 
                      key={interlocuteur.id} 
                      className={`${estSuspendu ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'} ${enTraitement ? 'opacity-50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[interlocuteur.type]}`}>
                          {TYPE_LABELS[interlocuteur.type]}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`text-sm font-medium ${estSuspendu ? 'text-gray-500' : 'text-gray-900'}`}>
                          {interlocuteur.raisonSociale}
                        </div>
                        {interlocuteur.siret && (
                          <div className="text-sm text-gray-500 font-mono">
                            SIRET: {interlocuteur.siret}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {contact ? (
                          <div className={estSuspendu ? 'text-gray-400' : ''}>
                            <div className={`text-sm ${estSuspendu ? 'text-gray-500' : 'text-gray-900'}`}>
                              {contact.prenom} {contact.nom}
                              {contact.fonction && (
                                <span className="text-gray-500"> - {contact.fonction}</span>
                              )}
                            </div>
                            {contact.email && (
                              <div className="text-sm text-gray-500">{contact.email}</div>
                            )}
                            {contact.telephone && (
                              <div className="text-sm text-gray-500">{contact.telephone}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Non renseigné</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {interlocuteur.actif ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Suspendu
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          {/* Bouton Voir / Modifier */}
                          <button
                            onClick={() => navigate(`/admin/interlocuteurs/${interlocuteur.id}`)}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Voir ou modifier"
                            disabled={enTraitement}
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          
                          {/* Bouton Suspendre / Réactiver */}
                          {interlocuteur.actif ? (
                            <button
                              onClick={() => handleSuspendre(interlocuteur)}
                              className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Suspendre"
                              disabled={enTraitement}
                            >
                              <PauseCircle className="w-5 h-5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactiver(interlocuteur)}
                              className="p-2 text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                              title="Réactiver"
                              disabled={enTraitement}
                            >
                              <PlayCircle className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
